import React from 'react';
import { OnboardingStepProps } from './types';

const OnboardingStep: React.FC<OnboardingStepProps> = ({
  step,
  totalSteps,
  title,
  description,
  primaryAction,
  secondaryAction,
  onNext,
  onSkip,
  onPrevious,
}) => {
  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-jobesta-gray-90 mb-3">
          {title}
        </h2>
        <p className="text-base md:text-lg text-jobesta-gray-70 leading-relaxed">
          {description}
        </p>
      </div>

      {/* Progress Indicator */}
      <div className="flex gap-2 mb-8">
        {Array.from({ length: totalSteps }).map((_, index) => (
          <div
            key={index}
            className={`h-2 flex-1 rounded-full transition-all duration-300 ${
              index <= step
                ? 'bg-jobesta-blue'
                : 'bg-jobesta-gray-40'
            }`}
            aria-label={`Step ${index + 1} of ${totalSteps}${index <= step ? ' completed' : ''}`}
          />
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        {/* Skip Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSkip();
          }}
          className="px-4 py-2 text-sm md:text-base text-jobesta-gray-70 hover:text-jobesta-gray-90 transition-colors font-medium order-3 sm:order-1"
        >
          Skip
        </button>

        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 order-1 sm:order-2">
          {/* Previous Button (only show after first step) */}
          {step > 0 && onPrevious && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPrevious();
              }}
              className="px-6 py-3 text-sm md:text-base text-jobesta-gray-70 hover:text-jobesta-gray-90 border border-jobesta-gray-40 rounded-jobesta hover:border-jobesta-gray-60 transition-colors font-medium"
            >
              Previous
            </button>
          )}

          {/* Primary Action Button (if provided) OR Next/Get Started Button */}
          {primaryAction ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                primaryAction.onClick();
              }}
              className="px-6 py-3 text-sm md:text-base bg-jobesta-blue text-white rounded-jobesta hover:bg-jobesta-darkblue transition-colors font-medium shadow-jobesta hover:shadow-jobesta-hover"
            >
              {primaryAction.label}
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onNext();
              }}
              className="px-6 py-3 text-sm md:text-base bg-jobesta-blue text-white rounded-jobesta hover:bg-jobesta-darkblue transition-colors font-medium shadow-jobesta hover:shadow-jobesta-hover"
            >
              {step < totalSteps - 1 ? 'Next' : 'Get Started'}
            </button>
          )}
        </div>
      </div>

      {/* Step Counter (mobile only) */}
      <div className="mt-4 text-center text-sm text-jobesta-gray-60 sm:hidden">
        Step {step + 1} of {totalSteps}
      </div>
    </div>
  );
};

export default OnboardingStep;
